import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import Dialog from '@material-ui/core/Dialog'
import Button from '@material-ui/core/Button'
import Popper from '../components/Popper'
import classnames from 'classnames'
import { WithCameraContext } from '../contexts/cameraContext'
import Checkbox from '@material-ui/core/Checkbox'
import { ReactComponent as ConnectionIcon } from '../images/camera-tutorials-3.svg'
import { ReactComponent as CongratulationsIcon } from '../images/camera-tutorials-4.svg'
import { ReactComponent as CheckboxIcon } from '../images/checkbox_empty.svg'
import { ReactComponent as CheckboxCheckIcon } from '../images/checkbox_check.svg'

import '../styles/cameraTutorials.scss'

const CameraTutorials = ({
    disabled = false,
    currentStep,
    onUpdated,
    onClose,
    cameraStreamComponent,
    cameraSwitchRef,
    motionDetectionRef,
    setDisabled,
    cameraContext,
}) => {
    const { isPreview, enableDetection } = cameraContext
    const [isWatch, setWatch] = useState(false)
    const steps = [
        {
            name: '1',
            target: (
                <div className="camera-container-list tutorials">
                    {cameraStreamComponent}
                </div>
            ),
            title: <FormattedMessage id="camera_tutorial_title_1" />,
            description: <FormattedMessage id="camera_tutorial_text_1" />,
            tooltip: {
                ref: cameraSwitchRef,
                text: <FormattedMessage id="click_here" />,
                show: !isPreview,
                placement: 'bottom',
                offset: '0,5',
            },
            disabled: !isPreview || disabled,
            onFinish: () => setDisabled(false),
        },
        {
            name: '2',
            target: (
                <div className="camera-container-list tutorials">
                    {cameraStreamComponent}
                </div>
            ),
            title: <FormattedMessage id="camera_tutorial_title_2" />,
            description: <FormattedMessage id="camera_tutorial_text_2" />,
            tooltip: {
                ref: motionDetectionRef,
                text: <FormattedMessage id="click_here" />,
                show: !enableDetection && isPreview,
                placement: 'top',
                offset: '0,10',
            },
            disabled: !enableDetection || disabled,
            skipStep: false,
        },
        {
            name: '3',
            target: (
                <div style={{ marginBottom: -16, position: 'relative' }}>
                    <ConnectionIcon />
                    <div className="qrcode">
                        <div className="quick-download">
                            <FormattedMessage id="quick_download" />
                        </div>
                    </div>
                </div>
            ),
            title: <FormattedMessage id="camera_tutorial_title_3" />,
            description: (
                <>
                    <div>
                        <FormattedMessage id="camera_tutorial_text_3" />
                    </div>
                    <div className="hint">
                        <Checkbox
                            className="checkbox"
                            size="small"
                            icon={<CheckboxIcon />}
                            checkedIcon={<CheckboxCheckIcon />}
                            onChange={() => setWatch(!isWatch)}
                            checked={isWatch}
                        />
                        <span
                            className="text"
                            onClick={() => setWatch(!isWatch)}
                        >
                            <FormattedMessage id="camera_tutorial_checkbox_3" />
                        </span>
                    </div>
                </>
            ),
            disabled: !isWatch,
            skip: !isWatch,
        },
        {
            name: '4',
            target: <CongratulationsIcon style={{ marginBottom: -16 }} />,
            title: <FormattedMessage id="camera_tutorial_title_4" />,
            description: <FormattedMessage id="camera_tutorial_text_4" />,
            disabled: false,
        },
    ]

    useEffect(() => {
        if (currentStep >= steps.length || currentStep < 0) {
            onClose && onClose()
        } else {
            window.eventManager.sendUserMessage(
                `webcamera_onboard_tutorial_step_${currentStep + 1}`
            )
        }
    }, [currentStep])

    function buildTutorialSteps(steps) {
        const elems = new Array(steps.length * 2 - 1)
        for (let i = 0; i < steps.length; i++) {
            const index = i * 2
            const step = steps[i]
            if (step.skipStep) continue
            const finish = step.finish || i <= currentStep
            if (index - 1 >= 0) {
                elems[index - 1] = (
                    <div
                        className={classnames('line', { finish })}
                        key={`line-${i}`}
                    />
                )
            }
            elems[index] = (
                <div
                    key={`step-${step.name}`}
                    className={classnames('circle', { finish })}
                >
                    {step.name}
                </div>
            )
        }
        return elems
    }

    function getStep(step) {
        return step < steps.length ? steps[step] : null
    }

    function nextStep() {
        let nextStep = currentStep + 1
        while (getStep(nextStep)?.skipStep) {
            nextStep++
        }
        onUpdated(nextStep)
        step.onFinish && step.onFinish()
    }

    const step = getStep(currentStep)
    const isLast = currentStep === steps.length - 1
    if (!step) {
        return null
    }
    return (
        <Dialog
            className="camera-tutorials-modal"
            onClose={() => {}}
            open={true}
        >
            {step?.tooltip && step?.tooltip?.ref?.current && (
                <>
                    <Popper
                        disablePortal
                        open={step?.tooltip?.show}
                        anchorEl={step?.tooltip?.ref?.current}
                        placement={step?.tooltip?.placement}
                        modifiers={
                            step?.tooltip?.offset
                                ? {
                                      offset: {
                                          offset: step?.tooltip?.offset,
                                      },
                                  }
                                : null
                        }
                    >
                        {step?.tooltip?.text}
                    </Popper>
                </>
            )}
            <div className="camera-tutorials">
                <div className="camera-tutorials-body">
                    <div className="row">{step?.target}</div>
                    <div className="row">{buildTutorialSteps(steps)}</div>
                    <div className="row">
                        <div className="title">{step?.title}</div>
                    </div>
                    <div className="row small">
                        <div className="description">{step?.description}</div>
                    </div>
                </div>
                <div className="camera-tutorials-actions">
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={step?.disabled}
                        onClick={nextStep}
                    >
                        {isLast ? (
                            <FormattedMessage id="start" />
                        ) : (
                            <FormattedMessage id="next" />
                        )}
                    </Button>
                </div>
            </div>
            {step?.skip && (
                <Button color="primary" className="skip" onClick={nextStep}>
                    <FormattedMessage id="skip" />
                </Button>
            )}
        </Dialog>
    )
}

export default WithCameraContext(CameraTutorials)
